import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common";

const AT = /(^|\s)@([^\s@]{0,30})$/;

// Text box with @mention suggestions (people you follow first). Picking someone inserts "@Full Name " and records them in `mentions`.
export const MentionInput = ({ value, onChange, mentions, setMentions, multiline = false, className = "", testid, ...rest }) => {
  const ref = useRef(null);
  const [q, setQ] = useState(null);
  const [rows, setRows] = useState([]);
  const [hi, setHi] = useState(0);
  useEffect(() => {
    if (q === null) return undefined;
    const t = setTimeout(() => api.get("/people/mention", { params: { q } }).then((r) => { setRows(r.data); setHi(0); }).catch(() => setRows([])), 150);
    return () => clearTimeout(t);
  }, [q]);
  const track = (text, caret) => { const m = text.slice(0, caret).match(AT); setQ(m ? m[2] : null); if (!m) setRows([]); };
  const pick = (u) => {
    const el = ref.current;
    const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(AT, (all, sp) => `${sp}@${u.name} `);
    onChange(before + value.slice(caret));
    setMentions((xs) => (xs.some((x) => x.user_id === u.user_id) ? xs : [...xs, { user_id: u.user_id, name: u.name }]));
    setQ(null); setRows([]);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(before.length, before.length); }, 0);
  };
  const onKeyDown = (e) => {
    if (rows.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % rows.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h - 1 + rows.length) % rows.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(rows[hi]); return; }
      if (e.key === "Escape") { e.preventDefault(); setQ(null); setRows([]); return; }
    }
    rest.onKeyDown?.(e);
  };
  const Tag = multiline ? "textarea" : "input";
  return (
    <div className="relative min-w-0 flex-1">
      <Tag ref={ref} {...rest} value={value} className={className} data-testid={testid} onKeyDown={onKeyDown}
        onChange={(e) => { onChange(e.target.value); track(e.target.value, e.target.selectionStart); }}
        onBlur={() => setTimeout(() => { setQ(null); setRows([]); }, 150)} />
      {rows.length > 0 && (
        <ul role="listbox" className="absolute left-0 top-full z-40 mt-1 max-h-64 w-72 max-w-[85vw] overflow-y-auto rounded-xl border border-border bg-white py-1 shadow-lg" data-testid={`${testid}-mentions`}>
          {rows.map((u, i) => (
            <li key={u.user_id} role="option" aria-selected={i === hi}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(u); }} data-testid={`mention-option-${u.user_id}`}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${i === hi ? "bg-[#F5F1EB]" : "hover:bg-black/5"}`}>
                <Avatar user={u} size={26} /><span className="min-w-0 flex-1 truncate font-medium">{u.name}</span>
                {u.following && <span className="text-[10px] uppercase tracking-wide text-slate-500">Following</span>}
              </button>
            </li>))}
        </ul>)}
    </div>
  );
};

// Only mentions whose "@Name" is still in the text get sent.
export const mentionIds = (text, mentions) => mentions.filter((m) => text.includes(`@${m.name}`)).map((m) => m.user_id);

// Shows "@Name" as a link to that member's profile.
export const MentionText = ({ text, mentions }) => {
  if (!mentions?.length || !text) return text || null;
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(@(?:${names.map((m) => esc(m.name)).join("|")}))`, "g"));
  return parts.map((p, i) => {
    const m = p.startsWith("@") && names.find((x) => `@${x.name}` === p);
    return m ? <Link key={i} to={`/u/${m.user_id}`} className="font-medium text-[hsl(var(--social))] hover:underline" data-testid={`mention-link-${m.user_id}`}>{p}</Link> : p;
  });
};
